import React, { useState, useEffect, useCallback } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

import {
  ShieldAlert, ScanSearch, AlertOctagon, AlertTriangle, AlertCircle,
  CheckCircle, Loader2, Filter, ChevronRight, RefreshCw, Zap, Eye, X
} from 'lucide-react';

const SEVERITY_META = {
  critical: { label: 'حرج / Critical', icon: AlertOctagon,    cls: 'bg-rose-500/20 text-rose-300 border-rose-500/40',     dot: 'bg-rose-500' },
  high:     { label: 'عالي / High',    icon: AlertTriangle,   cls: 'bg-orange-500/20 text-orange-300 border-orange-500/40', dot: 'bg-orange-500' },
  medium:   { label: 'متوسط / Medium', icon: AlertCircle,     cls: 'bg-amber-500/20 text-amber-300 border-amber-500/40',   dot: 'bg-amber-500' },
  low:      { label: 'منخفض / Low',    icon: AlertCircle,     cls: 'bg-slate-500/20 text-slate-300 border-slate-500/40',   dot: 'bg-slate-500' },
};

const TYPE_LABELS = {
  duplicate_payment:        'مدفوعات مكررة / Duplicate Payment',
  unbalanced_journal:       'قيد غير متوازن / Unbalanced Journal',
  revenue_misposting:       'خطأ في حساب إيراد / Revenue Misposting',
  large_journal_entry:      'قيد ضخم / Large Journal',
  unusual_expense:          'مصروف شاذ / Unusual Expense',
  after_hours_posting:      'قيد خارج الدوام / After-hours Posting',
  rapid_vendor_payments:    'دفعات متتالية لمورد / Rapid Vendor Payments',
  negative_asset_balance:   'رصيد أصل سالب / Negative Asset Balance',
};

const AuditIntelligence = ({ user }) => {
  const { t } = useTranslation();
  const [dashboard, setDashboard] = useState(null);
  const [executive, setExecutive] = useState(null);
  const [anomalies, setAnomalies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [filterSev, setFilterSev] = useState('all');
  const [filterStatus, setFilterStatus] = useState('open');
  const [selected, setSelected] = useState(null);
  const isAdmin = user?.role === 'admin';

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const [d, a, e] = await Promise.all([
        axiosInstance.get('/audit-intel/dashboard'),
        axiosInstance.get(`/audit-intel/anomalies?status=${filterStatus}${filterSev !== 'all' ? `&severity=${filterSev}` : ''}&limit=500`),
        axiosInstance.get('/audit-intel/executive'),
      ]);
      setDashboard(d.data);
      setAnomalies(a.data);
      setExecutive(e.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [filterSev, filterStatus]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleRun = async () => {
    if (!isAdmin) return;
    setRunning(true);
    try {
      const r = await axiosInstance.post('/audit-intel/run');
      toast.success(`اكتشف ${r.data.total_findings} حالة / Detected ${r.data.total_findings} findings`);
      window.dispatchEvent(new Event('audit-refresh'));
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally {
      setRunning(false);
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await axiosInstance.put(`/audit-intel/anomalies/${id}/status?new_status=${newStatus}`);
      toast.success(t('auditintelligence.updated'));
      window.dispatchEvent(new Event('audit-refresh'));
      setSelected(null);
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const KPI = ({ sev, count }) => {
    const m = SEVERITY_META[sev];
    const Icon = m.icon;
    return (
      <div className={`rounded-xl p-4 border ${m.cls}`} data-testid={`kpi-${sev}`}>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider opacity-80">{m.label}</p>
            <p className="text-3xl font-bold mt-1">{count}</p>
          </div>
          <Icon className="w-10 h-10 opacity-60" />
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4" data-testid="audit-intel-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <ShieldAlert size={24} className="text-rose-600" />{t('auditintelligence.audit_intelligence_engine')}</h1>
        {dashboard?.last_run && (
          <span className="text-xs text-gray-500">
            آخر تشغيل / Last run: {new Date(dashboard.last_run.started_at).toLocaleString('en')}
            {' · '}{dashboard.last_run.triggered_by_name}
          </span>
        )}
      </div>

      {/* Hero */}
      <div className="bg-gradient-to-br from-slate-900 via-rose-950 to-slate-900 rounded-2xl shadow-xl p-6 border border-rose-900/30">
        <p className="text-rose-300 text-xs font-semibold uppercase tracking-wider mb-2">P6 Engine</p>
        <h2 className="text-2xl font-bold text-white mb-2">
          كاشف الأنماط المالية الشاذة
        </h2>
        <p className="text-slate-300 text-sm max-w-4xl">
          8 محرّكات كشف: مدفوعات مكررة · قيود غير متوازنة · أخطاء حسابات الإيرادات · قيود ضخمة ·
          مصاريف شاذة (statistical outliers) · قيود خارج الدوام · دفعات متتالية لمورد ·
          أرصدة أصول سالبة.
        </p>
        <div className="flex flex-wrap items-end gap-3 mt-5">
          <button
            onClick={handleRun}
            disabled={running || !isAdmin}
            data-testid="run-audit-btn"
            className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-gradient-to-r from-rose-600 to-orange-600 text-white font-bold disabled:opacity-50 hover:opacity-90 shadow-lg shadow-rose-900/40 transition"
          >
            {running ? <Loader2 size={16} className="animate-spin" /> : <Zap size={16} />}
            تشغيل المسح / Run Full Audit
          </button>
          <button
            onClick={fetchAll}
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-slate-700 text-slate-200 hover:bg-slate-600 transition"
            data-testid="refresh-btn"
          >
            <RefreshCw size={14} />{t('auditintelligence.refresh')}</button>
        </div>
        {!isAdmin && (
          <p className="text-xs text-amber-300 mt-2">يلزم صلاحية Admin لتشغيل المسح.</p>
        )}
      </div>

      {/* KPI grid — 5 columns including Total Open */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div className="rounded-xl p-4 border bg-slate-900 text-white border-slate-700" data-testid="kpi-total">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider opacity-80">Total Open</p>
              <p className="text-3xl font-bold mt-1" data-testid="total-open">{dashboard?.total_open ?? '—'}</p>
            </div>
            <ShieldAlert className="w-10 h-10 opacity-60" />
          </div>
        </div>
        <KPI sev="critical" count={dashboard?.by_severity?.critical ?? 0} />
        <KPI sev="high"     count={dashboard?.by_severity?.high ?? 0} />
        <KPI sev="medium"   count={dashboard?.by_severity?.medium ?? 0} />
        <KPI sev="low"      count={dashboard?.by_severity?.low ?? 0} />
      </div>

      {/* P6.2 Executive Dashboard */}
      {executive && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="executive-panel">
          {/* Risk Score gauge */}
          <div className={`rounded-2xl shadow-xl p-6 border bg-gradient-to-br ${
            executive.health_color === 'emerald' ? 'from-emerald-500 to-emerald-700 border-emerald-300' :
            executive.health_color === 'blue' ? 'from-blue-500 to-indigo-600 border-blue-300' :
            executive.health_color === 'amber' ? 'from-amber-500 to-orange-600 border-amber-300' :
            'from-rose-500 to-rose-700 border-rose-300'
          } text-white`}>
            <p className="text-xs uppercase tracking-wider opacity-80 font-semibold mb-2">Financial Risk Score</p>
            <div className="flex items-end gap-2">
              <p className="text-6xl font-bold leading-none" data-testid="risk-score">{executive.risk_score}</p>
              <p className="text-sm opacity-80 mb-2">/ 100</p>
            </div>
            <p className="text-base font-semibold mt-2" data-testid="health-label">{executive.health_label}</p>
            <div className="mt-4 h-2 bg-white/20 rounded-full overflow-hidden">
              <div className="h-full bg-white/80 transition-all" style={{ width: `${executive.risk_score}%` }} />
            </div>
          </div>

          {/* Today's pulse */}
          <div className="rounded-2xl shadow-sm border border-gray-100 bg-white p-6">
            <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-3">Today / اليوم</p>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">{t('auditintelligence.journals_posted')}</span>
                <span className="text-2xl font-bold text-gray-900">{executive.today.journals_posted}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">{t('auditintelligence.unbalanced')}</span>
                <span className={`text-2xl font-bold ${(executive.today.open_unbalanced ?? executive.today.unbalanced_journals) > 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
                  {executive.today.open_unbalanced ?? executive.today.unbalanced_journals}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">{t('auditintelligence.bank_recon')}</span>
                <span className={`text-2xl font-bold ${
                  executive.bank.reconciliation_rate >= 75 ? 'text-emerald-600' :
                  executive.bank.reconciliation_rate >= 50 ? 'text-amber-600' : 'text-rose-600'
                }`}>{executive.bank.reconciliation_rate}%</span>
              </div>
              <div className="pt-2 border-t border-gray-100 flex items-center justify-between">
                <span className="text-sm text-gray-600">{t('auditintelligence.current_period')}</span>
                <span className={`text-xs font-bold px-2 py-1 rounded ${
                  executive.current_period.status === 'closed' ? 'bg-rose-100 text-rose-700' :
                  executive.current_period.status === 'frozen' ? 'bg-blue-100 text-blue-700' :
                  'bg-emerald-100 text-emerald-700'
                }`}>{executive.current_period.period} · {executive.current_period.status}</span>
              </div>
            </div>
          </div>

          {/* Top Critical Alerts */}
          <div className="rounded-2xl shadow-sm border border-gray-100 bg-white p-6">
            <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-3 flex items-center gap-2">
              <AlertOctagon size={14} className="text-rose-600" />{t('auditintelligence.top_alerts')}</p>
            {executive.top_alerts.length === 0 ? (
              <p className="text-sm text-emerald-600 flex items-center gap-2 py-4">
                <CheckCircle size={18} />{t('auditintelligence.no_active_alerts')}</p>
            ) : (
              <div className="space-y-2">
                {executive.top_alerts.slice(0, 4).map(a => {
                  const m = SEVERITY_META[a.severity] || SEVERITY_META.low;
                  return (
                    <div key={a.id} className="flex items-start gap-2 p-2 rounded-lg hover:bg-gray-50">
                      <span className={`w-2 h-2 rounded-full ${m.dot} mt-1.5`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-gray-700 truncate">{a.description}</p>
                        <p className="text-[10px] text-gray-400 mt-0.5">{a.severity} · {a.score}/100</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}



      {/* Top types */}
      {dashboard?.top_types?.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <h2 className="font-bold text-base mb-3 text-gray-800">{t('auditintelligence.top_anomaly_types')}</h2>
          <div className="space-y-2">
            {dashboard.top_types.map(t => {
              const maxCount = Math.max(...dashboard.top_types.map(x => x.count));
              const pct = (t.count / maxCount) * 100;
              return (
                <div key={t.type} className="flex items-center gap-3">
                  <span className="text-xs text-gray-600 w-64 shrink-0">{TYPE_LABELS[t.type] || t.type}</span>
                  <div className="flex-1 h-6 bg-gray-100 rounded-md overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-rose-500 to-orange-500 flex items-center px-2"
                         style={{ width: `${Math.max(pct, 8)}%` }}>
                      <span className="text-xs font-bold text-white">{t.count}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Anomalies list */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between flex-wrap gap-2">
          <h2 className="font-bold text-base text-gray-800 flex items-center gap-2">
            <ScanSearch size={18} className="text-rose-600" />{t('auditintelligence.detected_anomalies')}<span className="text-xs text-gray-500 font-normal">({anomalies.length})</span>
          </h2>
          <div className="flex items-center gap-2">
            <Filter size={14} className="text-gray-400" />
            <select value={filterSev} onChange={e => setFilterSev(e.target.value)}
                    className="text-xs border border-gray-200 rounded-lg px-2 py-1.5"
                    data-testid="filter-severity">
              <option value="all">{t('auditintelligence.all')}</option>
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
                    className="text-xs border border-gray-200 rounded-lg px-2 py-1.5"
                    data-testid="filter-status">
              <option value="open">{t('auditintelligence.open')}</option>
              <option value="acknowledged">Acknowledged</option>
              <option value="resolved">Resolved</option>
              <option value="false_positive">False positive</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div className="py-16 text-center text-gray-400">
            <Loader2 className="w-6 h-6 animate-spin mx-auto" />
          </div>
        ) : anomalies.length === 0 ? (
          <div className="py-16 text-center text-gray-400">
            <CheckCircle className="w-10 h-10 mx-auto mb-2 text-emerald-500" />
            <p className="font-semibold">{t('auditintelligence.no_anomalies')}</p>
            <p className="text-xs mt-1">All clear — run a fresh scan to check.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {anomalies.map(a => {
              const m = SEVERITY_META[a.severity] || SEVERITY_META.low;
              return (
                <button
                  key={a.id}
                  onClick={() => setSelected(a)}
                  className="w-full px-4 py-3 hover:bg-rose-50/30 transition flex items-center gap-3 text-left"
                  data-testid={`anomaly-${a.id}`}
                >
                  <span className={`w-2.5 h-2.5 rounded-full ${m.dot}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`px-2 py-0.5 text-xs font-bold rounded ${m.cls} border-0`}>
                        {a.severity.toUpperCase()} · {a.score}
                      </span>
                      <span className="text-xs text-gray-500">{TYPE_LABELS[a.type] || a.type}</span>
                      <span className="text-[10px] text-gray-400">{(a.created_at || '').slice(0, 16).replace('T', ' ')}</span>
                    </div>
                    <p className="text-sm text-gray-800 mt-1 truncate">{a.description}</p>
                  </div>
                  <ChevronRight size={16} className="text-gray-400 shrink-0" />
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Detail modal */}
      {selected && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b flex items-center justify-between bg-gradient-to-r from-rose-600 to-orange-600 text-white">
              <div className="flex items-center gap-3">
                <Eye size={20} />
                <div>
                  <h2 className="text-base font-bold">{TYPE_LABELS[selected.type] || selected.type}</h2>
                  <p className="text-xs text-rose-100 mt-0.5">
                    {selected.severity.toUpperCase()} · score {selected.score} · {selected.status}
                  </p>
                </div>
              </div>
              <button onClick={() => setSelected(null)} className="p-2 rounded-lg hover:bg-white/10" data-testid="close-detail">
                <X size={18} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Description</p>
                <p className="text-sm text-gray-800">{selected.description}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Entity</p>
                  <p className="text-sm text-gray-800 font-mono">{selected.entity_type} · {selected.entity_id || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Detected at</p>
                  <p className="text-sm text-gray-800 font-mono">{(selected.created_at || '').slice(0, 19).replace('T', ' ')}</p>
                </div>
              </div>

              {selected.metadata && Object.keys(selected.metadata).length > 0 && (
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Metadata</p>
                  <div className="bg-gray-50 rounded-lg p-3 text-xs font-mono text-gray-700 overflow-x-auto">
                    <pre>{JSON.stringify(selected.metadata, null, 2)}</pre>
                  </div>
                </div>
              )}

              {selected.type === 'negative_asset_balance' &&
                (selected.metadata?.system_code === 'CASH_EXPENSES' ||
                 selected.metadata?.account_name === 'Cash Expenses') && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="cash-settle-hint">
                  <p className="text-sm text-amber-900 font-medium">
                    هذا الرصيد السالب حقيقي: المصروفات النقدية تُصرف من 11150 دون تسجيل مصدر تمويلها.
                    الحل: تسوية الرصيد على "جاري المالك" بضغطة واحدة من دفتر المصروفات النقدية.
                  </p>
                  <button
                    data-testid="goto-owner-settlement-btn"
                    onClick={() => { setSelected(null); window.location.href = '/cash-expenses-ledger'; }}
                    className="mt-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-sm font-medium"
                  >
                    اذهب للتسوية الآن ← دفتر المصروفات النقدية
                  </button>
                </div>
              )}

              {selected.status_note && (
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Note</p>
                  <p className="text-sm text-gray-700">{selected.status_note}</p>
                </div>
              )}
            </div>

            {isAdmin && selected.status === 'open' && (
              <div className="px-6 py-4 border-t flex items-center justify-end gap-2 bg-gray-50">
                <button
                  onClick={() => handleStatusChange(selected.id, 'false_positive')}
                  className="px-3 py-1.5 text-xs rounded-lg bg-slate-200 hover:bg-slate-300 text-slate-700 font-medium"
                  data-testid="mark-false-positive"
                >{t('auditintelligence.false_positive')}</button>
                <button
                  onClick={() => handleStatusChange(selected.id, 'acknowledged')}
                  className="px-3 py-1.5 text-xs rounded-lg bg-amber-500 hover:bg-amber-600 text-white font-medium"
                  data-testid="mark-acknowledged"
                >{t('auditintelligence.acknowledge')}</button>
                <button
                  onClick={() => handleStatusChange(selected.id, 'resolved')}
                  className="px-3 py-1.5 text-xs rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-medium"
                  data-testid="mark-resolved"
                >{t('auditintelligence.resolved')}</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditIntelligence;
